import { useMutation } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { Opening } from 'api-clients/monolith/models/Opening';
import { SourcingService } from 'api-clients/monolith/services/SourcingService';
import _isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import ChartBar from '@/assets/icons/chart-bar.svg';
import { useToaster } from '@/components/base/toaster';
import { ActionToast } from '@/components/ui/action-toast';

interface RowsSelectProps {
  table: Table<Opening>;
  refetchOpenings: () => Promise<void>;
}

export function RowsSelect({ table, refetchOpenings }: RowsSelectProps) {
  const { rowSelection } = table.getState();
  const { notifySuccess, notifyError } = useToaster();
  const intl = useIntl();

  const selectedIds = useMemo(
    () => (_isEmpty(rowSelection) ? [] : Object.keys(rowSelection)),
    [rowSelection],
  );

  const { mutate: updateSelection } = useMutation({
    mutationFn: () =>
      SourcingService.postInternalApiSourcingOpeningsMarkForSourcing({
        selected_opening_ids: selectedIds,
      }),
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Selected openings updated successfully!',
        }),
      });
      table.resetRowSelection();
      await refetchOpenings();
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update the selected openings!',
        }),
      });
    },
  });

  const onSelectedOpeningsSubmit = () => {
    updateSelection();
  };

  if (_isEmpty(selectedIds)) return <></>;

  return (
    <ActionToast
      actionButtons={[
        {
          label: intl.formatMessage({
            defaultMessage: 'Add selection to dashboard',
          }),
          onClick: onSelectedOpeningsSubmit,
          icon: ChartBar,
        },
      ]}
    />
  );
}
